import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import IconButton from 'components/common/IconButton';
import PageHeader from 'components/common/PageHeader';
import FalconComponentCard from 'components/common/FalconComponentCard';
import departments from '../../data/departments';
import venues from '../../data/venues';

const EditCalendarEvent = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    department: '',
    venue: 'TBA',
    startDateDD: null,
    startDateMM: null,
    startDateYYYY: null,
    endDateDD: null,
    endDateMM: null,
    endDateYYYY: null,
    startTimeHH: null,
    startTimeMM: null,
    endTimeHH: null,
    endTimeMM: null,
    enabled: true,
    user_id: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const uid = user.uid;
        setFormData({
          ...formData,
          user_id: uid,
        });
      } else {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const eventId = window.location.pathname.split('/').pop();
      if (eventId) {
        try {
          const apiUrl = `${process.env.REACT_APP_API_URL}`;

          const response = await axios.get(`${apiUrl}/calendar-events/id/${eventId}`);

          if (response.data) {
            const eventData = response.data.data[0];
            setFormData({
              title: eventData.title,
              enabled: eventData.enabled === '1' ? true : false,
              venue: eventData.venue,
              id: eventData.id,
              department: eventData.department,
              startDateDD: eventData.start_date.split('/')[0],
              startDateMM: eventData.start_date.split('/')[1],
              startDateYYYY: eventData.start_date.split('/')[2],
              startTimeHH: eventData.start_time.split(':')[0],
              startTimeMM: eventData.start_time.split(':')[1],
              endDateDD: eventData.end_date.split('/')[0],
              endDateMM: eventData.end_date.split('/')[1],
              endDateYYYY: eventData.end_date.split('/')[2],
              endTimeHH: eventData.end_time.split(':')[0],
              endTimeMM: eventData.end_time.split(':')[1],
            });
            setLoading(false);
          }
        } catch (err) {
          console.log('Error', err);
          setLoading(false);
        }
      } else {
        navigate('/calendar-events/list');
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const startDate = `${formData.startDateDD}/${formData.startDateMM}/${formData.startDateYYYY}`;
    const endDate = `${formData.endDateDD}/${formData.endDateMM}/${formData.endDateYYYY}`;

    const { startDateDD, startDateMM, startDateYYYY } = formData;
    const { endDateDD, endDateMM, endDateYYYY } = formData;

    const inputStartDate = new Date(startDateYYYY, startDateMM - 1, startDateDD);
    const inputEndDate = new Date(endDateYYYY, endDateMM - 1, endDateDD);

    const today = new Date();
    today.setHours(0, 0, 0, 0); // set time to 00:00:00.000

    try {
      if (inputEndDate < inputStartDate || startDateMM > 12 || startDateDD > 31 || endDateMM > 12 || endDateDD > 31) {
        toast.error('Start/End dates are invalid.');
        return;
      }
    } catch (error) {
      toast.error('Start/End dates are invalid.');
      return;
    }

    const { startTimeHH, startTimeMM } = formData;
    const { endTimeHH, endTimeMM } = formData;
    const startTime = `${startTimeHH}:${startTimeMM}`;
    const endTime = `${endTimeHH}:${endTimeMM}`;

    try {
      const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
      if (!timePattern.test(startTime) || !timePattern.test(endTime)) {
        toast.error('Event start/end time is invalid.');
        return;
      }
    } catch (error) {
      toast.error('Event start/end time is invalid.');
      return;
    }

    let formData2 = new FormData();
    formData2.append('title', formData.title);
    formData2.append('department', formData.department);
    formData2.append('resource_id', formData.department.split()[0]);
    formData2.append('venue', formData.venue);
    formData2.append('start_date', startDate);
    formData2.append('end_date', endDate);
    formData2.append('start_time', startTime);
    formData2.append('end_time', endTime);
    formData2.append('user_id', formData.user_id);
    formData2.append('enabled', formData.enabled);

    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}`;
      const id = window.location.pathname.split('/').pop();

      const response = await axios.put(`${apiUrl}/calendar-events/id/${id}`, formData2);

      if (response) {
        toast.success(`Calendar event updated successfully`, {
          theme: 'colored',
        });
        navigate('/calendar-events/list');
      }
    } catch (err) {
      toast.error(err.response?.data?.message ?? 'Error saving calendar event', {
        theme: 'colored',
        autoClose: false,
        closeButton: true,
      });
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  return (
    <>
      <PageHeader
        title="Edit calendar event"
        description="This updates a calendar event."
        className="mb-3"
      ></PageHeader>

      <FalconComponentCard>
        <FalconComponentCard.Header title="Details" light={false} noPreview={true}></FalconComponentCard.Header>
        {!loading && (
          <Form onSubmit={handleSubmit}>
            <FalconComponentCard.Body>
              <Form.Label>Title</Form.Label>
              <Form.Control
                size="lg"
                type="text"
                placeholder=""
                name="title"
                onChange={handleFieldChange}
                required
                autoComplete="off"
                value={formData.title}
              />
              <br />

              <Row>
                <Col lang="4">
                  <Form.Group controlId="formFileLg" className="mb-3" width="100%">
                    <Form.Label>Start date</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="DD"
                          name="startDateDD"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.startDateDD}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="MM"
                          name="startDateMM"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.startDateMM}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="4"
                          size="lg"
                          type="text"
                          placeholder="YYYY"
                          name="startDateYYYY"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.startDateYYYY}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
                <Col lang="4">
                  <Form.Group controlId="formFileLg" className="mb-3" width="100%">
                    <Form.Label>Start time</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="HH"
                          name="startTimeHH"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.startTimeHH}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="MM"
                          name="startTimeMM"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.startTimeMM}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
                <Col lang="4">
                  <Form.Group controlId="formFileLg" className="mb-3" width="100%">
                    <Form.Label>End date</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="DD"
                          name="endDateDD"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.endDateDD}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="MM"
                          name="endDateMM"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.endDateMM}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="4"
                          size="lg"
                          type="text"
                          placeholder="YYYY"
                          name="endDateYYYY"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.endDateYYYY}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
                <Col lang="4">
                  <Form.Group controlId="formFileLg" className="mb-3" width="100%">
                    <Form.Label>End time</Form.Label>
                    <Row>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="HH"
                          name="endTimeHH"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.endTimeHH}
                        />
                      </Col>
                      <Col>
                        <Form.Control
                          maxLength="2"
                          size="lg"
                          type="text"
                          placeholder="MM"
                          name="endTimeMM"
                          onChange={handleFieldChange}
                          required
                          defaultValue={formData.endTimeMM}
                        />
                      </Col>
                    </Row>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg="6">
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <br />
                    <Form.Check
                      name="enabled"
                      type="checkbox"
                      id="enabledCheckbox"
                      label="Enabled"
                      onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                      checked={formData.enabled}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg="6">
                  <Form.Group>
                    <Form.Label>Venue</Form.Label>
                    <Form.Select
                      size="md"
                      className="mb-3"
                      name="venue"
                      onChange={handleFieldChange}
                      defaultValue={formData.venue}
                    >
                      {venues.map((v, index) => (
                        <option key={index} value={v}>
                          {v}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg="6">
                  <Form.Group>
                    <Form.Label>Department</Form.Label>
                    <Form.Select
                      size="md"
                      className="mb-3"
                      name="department"
                      onChange={handleFieldChange}
                      defaultValue={formData.department}
                    >
                      {departments.map((dep, index) => (
                        <option key={index} value={dep.title}>
                          {dep.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col lg="6" className="mt-4 mb-3 ml-3">
                  <Form.Group>
                    <IconButton
                      className="mb-1"
                      variant="falcon-default"
                      size="lg"
                      icon="plus"
                      transform="shrink-3"
                      type="submit"
                    >
                      Submit
                    </IconButton>
                  </Form.Group>
                </Col>
              </Row>
            </FalconComponentCard.Body>
          </Form>
        )}
      </FalconComponentCard>
    </>
  );
};

export default EditCalendarEvent;
